import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ClientDropdown from '@src/components/ClientDropdown';
import { useMessage } from '@src/context/common';
import {
  buildLocalCampaigns,
  fetchLocalAdBatches,
  fetchLocalPlan,
  publishLocalAdBatch,
} from '@src/api/localAds';

// The floor is measured in owner-occupied homes, not people — a renter is not
// a customer. A lower floor gives more cities their own ad set.
const FLOORS = [50000, 35000, 25000, 20000, 15000, 12500, 10000, 7500, 5000];

// An ad set holding this much of its campaign leaves nothing inside the
// campaign to absorb a downturn in that one city.
const CONCENTRATION_FLAG = 0.35;

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const num = (n) => (n || 0).toLocaleString();
const floorLabel = (f) => (f >= 1000 ? `${f / 1000}k` : `${f}`);

function concentration(campaign) {
  const adSets = campaign.ad_sets || [];
  const total = adSets.reduce((sum, a) => sum + (a.population || 0), 0);
  if (!total) return null;
  const top = adSets.reduce((a, b) => ((a.population || 0) >= (b.population || 0) ? a : b));
  return { share: (top.population || 0) / total, city: top.city };
}

export default function LocalAdBuilderPage() {
  const message = useMessage();

  // ClientDropdown emits the client's id, not the object.
  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [floorIndex, setFloorIndex] = useState(FLOORS.indexOf(7500));
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [batches, setBatches] = useState([]);

  const floor = FLOORS[floorIndex];

  const loadPlan = useCallback(async () => {
    if (!clientId) {
      setPlan(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await fetchLocalPlan({ client_id: clientId, city_floor: floor });
      setPlan(data);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not load the plan.');
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, floor, message]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Default to the client's busiest account; the server already sorts by
  // recent spend. Reset when the client changes.
  useEffect(() => {
    const accounts = plan?.ad_accounts || [];
    if (!accounts.length) {
      setAdAccountId('');
      return;
    }
    setAdAccountId((current) =>
      accounts.some((a) => a.account_id === current) ? current : accounts[0].account_id,
    );
  }, [plan?.ad_accounts]);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await fetchLocalAdBatches();
      setBatches(Array.isArray(data) ? data : data?.results || []);
    } catch {
      // Secondary panel — a failure here should not take the page down.
      setBatches([]);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const campaigns = useMemo(
    () => (plan?.verticals || []).flatMap((v) => v.campaigns || []),
    [plan],
  );
  const totalMinimum = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.min_daily_budget_cents || 0), 0),
    [campaigns],
  );

  const built = plan?.built?.ad_sets > 0;
  const withAds = plan?.built?.with_ads || 0;
  const clientBatches = batches.filter(
    (b) => b.status === 'pending' && (b.rows || []).some((r) => r.client_name === clientName),
  );

  const handleBuild = async (dryRun) => {
    if (!clientId || !adAccountId) return;
    setBuilding(true);
    try {
      const { data } = await buildLocalCampaigns({
        client_id: clientId,
        ad_account_id: adAccountId,
        city_floor: floor,
        dry_run: dryRun,
      });
      message.success(
        dryRun
          ? `Dry run passed: ${data.campaigns} campaigns and ${data.units} ad sets would be created. Nothing was created.`
          : `Built ${data.campaigns} campaigns and ${data.units} ad sets, all paused.`,
      );
      if (!dryRun) loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'The build failed.');
    } finally {
      setBuilding(false);
    }
  };

  const handlePublish = async (batch, dryRun) => {
    try {
      const { data } = await publishLocalAdBatch(batch.id, {
        ad_account_id: adAccountId,
        dry_run: dryRun,
      });
      message.success(
        dryRun
          ? `Dry run: ${data.published} ads would publish, ${data.failed} would fail. Nothing was published.`
          : `Published ${data.published} ads, paused. ${data.failed} failed.`,
      );
      loadBatches();
      if (!dryRun) loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Publishing failed.');
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 3, maxWidth: 1100 }}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Local Ad Builder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
          Local campaigns go live in three steps: you build the campaigns, the
          creative app fills them with ads for each city, and you publish.
          Nothing reaches Facebook until you press a Build or Publish button, and
          everything lands paused.
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 2 }}>
              <ClientDropdown
                value={clientId || ''}
                onChange={(value, option) => {
                  setClientId(value || null);
                  setClientName(option?.label || '');
                }}
                label="Client"
              />
            </Box>
            <FormControl size="small" sx={{ flex: 1 }} disabled={!plan?.ad_accounts?.length}>
              <InputLabel id="local-ad-account">Ad account</InputLabel>
              <Select
                labelId="local-ad-account"
                label="Ad account"
                value={adAccountId}
                onChange={(e) => setAdAccountId(e.target.value)}
              >
                {(plan?.ad_accounts || []).map((a) => (
                  <MenuItem key={a.account_id} value={a.account_id}>
                    {a.account_name}
                    {a.spend_30d > 0 ? ` — $${Math.round(a.spend_30d).toLocaleString()} last 30d` : ' — no recent spend'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {clientId && plan && !plan.ad_accounts?.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This client has no active Facebook ad account linked, so nothing can be built.
            </Alert>
          )}
        </CardContent>
      </Card>

      {!clientId && (
        <Alert severity="info">Pick a client to start.</Alert>
      )}

      {clientId && (
        <>
          {/* ── Step 1 ───────────────────────────────────────────── */}
          <Step
            n={1}
            title="Build the campaigns"
            state={built ? 'done' : 'todo'}
            stateLabel={built ? `Built · ${num(plan.built.ad_sets)} ad sets` : 'Not built'}
          >
            {built ? (
              <Typography variant="body2" color="text.secondary">
                This client&apos;s local campaigns already exist, paused, with{' '}
                {num(plan.built.ad_sets)} city ad sets waiting for ads. Nothing
                to do here — move to step 2.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  One campaign per territory, one ad set per city. Choose how big a
                  city has to be to get its own ad set — lower means more cities.
                </Typography>

                <Box sx={{ px: 1, maxWidth: 640 }}>
                  <Slider
                    value={floorIndex}
                    min={0}
                    max={FLOORS.length - 1}
                    step={1}
                    marks={FLOORS.map((f, i) => ({ value: i, label: floorLabel(f) }))}
                    onChange={(_, v) => setFloorIndex(v)}
                    aria-label="City floor"
                  />
                </Box>

                <Stack direction="row" spacing={4} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <Stat
                    label="Smallest city included"
                    value={`${num(floor)} homes`}
                    hint="Owner-occupied homes. Renters aren't customers, so population overstates the market."
                  />
                  <Stat label="Campaigns" value={num(plan?.campaigns)} />
                  <Stat label="City ad sets" value={num(plan?.units)} />
                  <Stat
                    label="Minimum daily spend"
                    value={money(totalMinimum)}
                    hint="Facebook rejects a campaign whose daily budget can't fund its ad sets — about $1.01 each. This is the floor to keep them eligible, not your budget."
                  />
                </Stack>
                {loading && <LinearProgress sx={{ mt: 2 }} />}

                {plan?.warnings?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Fix before building</AlertTitle>
                    {plan.warnings.map((w) => (
                      <Typography key={w} variant="body2">
                        {w}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {campaigns.length > 0 && (
                  <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Territory</TableCell>
                          <TableCell align="right">City ad sets</TableCell>
                          <TableCell align="right">Min daily</TableCell>
                          <TableCell>Largest city</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaigns.map((c) => {
                          const conc = concentration(c);
                          const heavy = conc && conc.share >= CONCENTRATION_FLAG;
                          return (
                            <TableRow key={c.campaign_name} hover>
                              <TableCell>{c.territory}</TableCell>
                              <TableCell align="right">{num(c.ad_sets?.length)}</TableCell>
                              <TableCell align="right">{money(c.min_daily_budget_cents)}</TableCell>
                              <TableCell>
                                {conc && (
                                  <Tooltip
                                    title={
                                      heavy
                                        ? 'Most of this campaign is one city, so if that city turns, the whole campaign turns with it.'
                                        : ''
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      label={`${conc.city} · ${Math.round(conc.share * 100)}%`}
                                      color={heavy ? 'warning' : 'default'}
                                      variant={heavy ? 'filled' : 'outlined'}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {plan?.pages?.length > 0 && <PageCapacity pages={plan.pages} />}

                <Stack direction="row" spacing={1} sx={{ mt: 3 }} alignItems="center">
                  <Button
                    variant="outlined"
                    disabled={building || !adAccountId || !campaigns.length}
                    onClick={() => handleBuild(true)}
                  >
                    Dry run
                  </Button>
                  <Button
                    variant="contained"
                    disabled={building || !adAccountId || !campaigns.length || plan?.warnings?.length > 0}
                    onClick={() => handleBuild(false)}
                  >
                    {building ? <CircularProgress size={20} /> : `Build ${num(plan?.units)} ad sets (paused)`}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Dry run checks everything and creates nothing.
                  </Typography>
                </Stack>
              </>
            )}
          </Step>

          {/* ── Step 2 ───────────────────────────────────────────── */}
          <Step
            n={2}
            title="Creative app fills each city with ads"
            state={!built ? 'blocked' : withAds === plan.built.ad_sets ? 'done' : 'waiting'}
            stateLabel={
              !built
                ? 'Waiting on step 1'
                : `${num(withAds)} of ${num(plan.built.ad_sets)} cities have ads`
            }
          >
            <Typography variant="body2" color="text.secondary">
              {!built
                ? 'Once the campaigns exist, the creative app reads this client’s city list and generates ads for each city. There is nothing to do here until step 1 is done.'
                : 'The creative app reads this client’s city list, generates images and copy for each city, and sends them in. You don’t do anything here — ads it sends appear in step 3.'}
            </Typography>
            {built && plan.built.ad_sets > 0 && (
              <LinearProgress
                variant="determinate"
                value={(withAds / plan.built.ad_sets) * 100}
                sx={{ mt: 2, height: 6, borderRadius: 3, maxWidth: 480 }}
              />
            )}
          </Step>

          {/* ── Step 3 ───────────────────────────────────────────── */}
          <Step
            n={3}
            title="Review and publish"
            state={clientBatches.length ? 'todo' : 'waiting'}
            stateLabel={
              clientBatches.length
                ? `${clientBatches.length} batch${clientBatches.length === 1 ? '' : 'es'} to publish`
                : 'Nothing to publish yet'
            }
          >
            {clientBatches.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                When the creative app sends ads for this client, they land here
                for you to publish. Publishing creates them in Facebook, paused.
              </Typography>
            ) : (
              <Stack spacing={2} divider={<Divider flexItem />}>
                {clientBatches.map((batch) => (
                  <Box key={batch.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      spacing={1}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {(batch.rows || []).length} ads · batch {batch.external_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[...new Set((batch.rows || []).map((r) => r.raw_city).filter(Boolean))]
                            .slice(0, 6)
                            .join(', ')}
                          {(batch.rows || []).length > 6 ? '…' : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => handlePublish(batch, true)}>
                          Dry run
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={batch.unresolved > 0 || !adAccountId}
                          onClick={() => handlePublish(batch, false)}
                        >
                          Publish (paused)
                        </Button>
                      </Stack>
                    </Stack>
                    {batch.unresolved > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {batch.unresolved} of these ads don&apos;t match a city in this
                        client&apos;s campaigns, so publishing is blocked until they&apos;re
                        fixed or removed.
                      </Alert>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Step>
        </>
      )}
    </Stack>
  );
}

const STATE_CHIP = {
  done: { color: 'success', variant: 'filled' },
  todo: { color: 'primary', variant: 'filled' },
  waiting: { color: 'default', variant: 'outlined' },
  blocked: { color: 'default', variant: 'outlined' },
};

function Step({ n, title, state, stateLabel, children }) {
  const muted = state === 'blocked';
  return (
    <Card variant="outlined" sx={{ opacity: muted ? 0.7 : 1 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 14,
              bgcolor: state === 'done' ? 'success.main' : 'action.selected',
              color: state === 'done' ? 'success.contrastText' : 'text.primary',
              flexShrink: 0,
            }}
          >
            {state === 'done' ? '✓' : n}
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          <Chip size="small" label={stateLabel} {...STATE_CHIP[state]} />
        </Stack>
        <Box sx={{ pl: { xs: 0, sm: 5.5 } }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, hint }) {
  const body = (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

function PageCapacity({ pages }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="body2" fontWeight={600}>
        Facebook Pages
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Each Page can only hold so many ads. Local ads will go on whichever has the most room.
      </Typography>
      <Stack spacing={1.25} sx={{ maxWidth: 480 }}>
        {pages.map((page) => (
          <Box key={page.page_id}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2">
                {page.page_name}
                {page.reserved_for_local && (
                  <Chip size="small" label="held for local" variant="outlined" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {num(page.ads_used)} / {num(page.ad_tier)} ads
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(page.usage_pct, 100)}
              color={page.status === 'full' ? 'error' : page.status === 'warning' ? 'warning' : 'primary'}
              sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
